import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  MenuItem,
  Menu,
  Avatar,
} from '@mui/material';
import {
  Add,
  Warning,
  OpenInNew,
  MoreVert,
  MailOutline,
  Schedule,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosConfig';
import AddTILModal from './components/AddTILModal';
import { useAuth } from '../../../contexts/AuthContext';

const AVATAR_COLORS = ['#1976d2','#388e3c','#d32f2f','#7b1fa2','#f57c00','#0288d1','#c2185b','#00796b'];
const nameToColor = (name: string) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
const nameToInitials = (name: string) => {
  const parts = name.trim().split(' ');
  return parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase() : name.slice(0, 2).toUpperCase();
};

interface TILEntry {
  _id: string;
  hours: number;
  earnedDate: string;
  expiryDate: string;
  description: string;
  rotaEntryId?: string;
}

interface Personnel {
  _id: string;
  name: string;
  email?: string;
  rank: string;
  leaveEntitlement?: {
    annualLeaveTotal: number;
    annualLeaveCurrentYear?: number;
    annualLeaveUsed: number;
    annualLeaveCarryover?: number;
    annualLeaveCarryoverExpiry?: string;
    timeInLieuHours: number;
    timeInLieuUsed: number;
    timeInLieuEntries: TILEntry[];
    yearStartDate?: string;
    yearEndDate?: string;
  };
}

interface EmailDialogState {
  open: boolean;
  staffId: string;
  staffName: string;
  type: 'annual-leave' | 'til' | 'custom';
  subject: string;
  message: string;
}

const DEFAULT_EMAIL: EmailDialogState = {
  open: false,
  staffId: '',
  staffName: '',
  type: 'custom',
  subject: '',
  message: '',
};

const StaffBalancesTab: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [staff, setStaff] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [addTILModalOpen, setAddTILModalOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);

  const [emailDialog, setEmailDialog] = useState<EmailDialogState>(DEFAULT_EMAIL);
  const [emailSending, setEmailSending] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuStaffId, setMenuStaffId] = useState<string | null>(null);

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/api/personnel/staff/leave-balances');
      setStaff(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch staff data');
    } finally {
      setLoading(false);
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, staffId: string) => {
    setMenuAnchor(e.currentTarget);
    setMenuStaffId(staffId);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuStaffId(null);
  };

  const openEmailDialog = (staffId: string, type: EmailDialogState['type']) => {
    closeMenu();
    const person = staff.find(s => s._id === staffId);
    if (!person) return;

    const now = new Date();
    const year = now.getFullYear();
    const ent = person.leaveEntitlement;
    const carryover = ent?.annualLeaveCarryover || 0;
    const carryoverExpiry = ent?.annualLeaveCarryoverExpiry
      ? new Date(ent.annualLeaveCarryoverExpiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      : null;

    let subject = '';
    let message = '';

    if (type === 'annual-leave') {
      const remaining = (ent?.annualLeaveCurrentYear || ent?.annualLeaveTotal || 0) - (ent?.annualLeaveUsed || 0);
      subject = `Annual Leave Balance — ${year}`;
      message = `Hi ${person.name.split(' ')[0]},\n\nThis is a reminder that you currently have ${remaining} day${remaining !== 1 ? 's' : ''} of annual leave remaining for ${year}${carryover > 0 ? `, plus ${carryover} carry-over day${carryover !== 1 ? 's' : ''} expiring ${carryoverExpiry}` : ''}.\n\nPlease make sure to book your remaining leave before the year end.\n\nBest,\n${user?.name || 'IMC Management'}`;
    } else if (type === 'til') {
      const tilHours = parseFloat(((ent?.timeInLieuHours || 0) - (ent?.timeInLieuUsed || 0)).toFixed(2));
      const expiringEntries = (ent?.timeInLieuEntries || []).filter(e => {
        const exp = new Date(e.expiryDate);
        const soon = new Date();
        soon.setDate(soon.getDate() + 30);
        return exp > now && exp <= soon;
      });
      subject = `Time in Lieu — Expiry Reminder`;
      message = `Hi ${person.name.split(' ')[0]},\n\nYou currently have ${tilHours} hours of Time in Lieu accrued.${expiringEntries.length > 0 ? `\n\n${expiringEntries.length} TIL entr${expiringEntries.length !== 1 ? 'ies' : 'y'} will expire within the next 30 days — please arrange to take this time before it is lost.` : ''}\n\nBest,\n${user?.name || 'IMC Management'}`;
    } else {
      subject = '';
      message = '';
    }

    setEmailDialog({ open: true, staffId, staffName: person.name, type, subject, message });
  };

  const handleSendEmail = async () => {
    if (!emailDialog.subject.trim() || !emailDialog.message.trim()) return;
    setEmailSending(true);
    try {
      await axiosInstance.post(`/api/personnel/${emailDialog.staffId}/leave/notify`, {
        subject: emailDialog.subject,
        message: emailDialog.message,
        type: emailDialog.type,
        senderName: user?.name || 'IMC Management',
      });
      setSuccessMessage(`Email sent to ${emailDialog.staffName}`);
      setTimeout(() => setSuccessMessage(null), 4000);
      setEmailDialog(DEFAULT_EMAIL);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send email');
      setTimeout(() => setError(null), 4000);
    } finally {
      setEmailSending(false);
    }
  };

  const handleAddTILClick = (staffId: string) => {
    closeMenu();
    setSelectedStaffId(staffId);
    setAddTILModalOpen(true);
  };

  const handleAddTILConfirm = async (hours: number, description: string) => {
    if (!selectedStaffId) return;
    try {
      await axiosInstance.post(`/api/personnel/${selectedStaffId}/leave/add-til`, {
        hours,
        description,
        adminOverride: true,
      });
      setSuccessMessage('TIL hours added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setAddTILModalOpen(false);
      setSelectedStaffId(null);
      fetchStaff();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add TIL');
      setTimeout(() => setError(null), 3000);
    }
  };

  const getWarnings = (person: Personnel): string[] => {
    const warnings: string[] = [];
    const ent = person.leaveEntitlement;
    if (!ent) return warnings;

    const { annualLeaveTotal, annualLeaveUsed, timeInLieuEntries = [] } = ent;
    const now = new Date();
    const month = now.getMonth() + 1;
    const progress = month / 12;
    const expectedUsed = Math.round(annualLeaveTotal * progress);

    if (month >= 10 && annualLeaveUsed < annualLeaveTotal * 0.5) {
      warnings.push('High unused AL (year-end)');
    } else if (annualLeaveUsed < expectedUsed * 0.6) {
      warnings.push('Behind on AL usage');
    }

    const remaining = annualLeaveTotal - annualLeaveUsed;
    if (remaining <= 2) warnings.push('Almost out of AL');
    else if (remaining <= 5) warnings.push('Low AL balance');

    const soon = new Date();
    soon.setDate(soon.getDate() + 30);
    const expiringTIL = timeInLieuEntries.filter(
      e => new Date(e.expiryDate) <= soon && new Date(e.expiryDate) >= now
    );
    if (expiringTIL.length > 0) warnings.push(`${expiringTIL.length} TIL expiring soon`);

    return warnings;
  };

  const calculateTILDays = (hours: number): string => {
    const days = Math.floor(hours / 12);
    const remainingHours = hours % 12;
    if (days === 0) return `${remainingHours}h`;
    if (remainingHours === 0) return `${days}d`;
    return `${days}d ${remainingHours}h`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const menuPerson = menuStaffId ? staff.find(s => s._id === menuStaffId) : null;

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Staff Member</TableCell>
              <TableCell>Annual Leave</TableCell>
              <TableCell>Time in Lieu</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.map((person) => {
              const warnings = getWarnings(person);
              const ent = person.leaveEntitlement;
              const currentYearTotal = ent?.annualLeaveCurrentYear || ent?.annualLeaveTotal || 0;
              const alUsed = ent?.annualLeaveUsed || 0;
              const alRemaining = currentYearTotal - alUsed;
              const carryover = ent?.annualLeaveCarryover || 0;
              const carryoverExpiry = ent?.annualLeaveCarryoverExpiry
                ? new Date(ent.annualLeaveCarryoverExpiry)
                : null;
              const carryoverActive = carryover > 0 && carryoverExpiry && carryoverExpiry > new Date();

              const totalH = Number(ent?.timeInLieuHours ?? 0);
              const usedH = Number(ent?.timeInLieuUsed ?? 0);
              const tilHours = parseFloat((totalH - usedH).toFixed(2));

              return (
                <TableRow
                  key={person._id}
                  hover
                  onClick={() => navigate(`/admin/leave-management/staff/${person._id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        sx={{ width: 36, height: 36, bgcolor: nameToColor(person.name), fontSize: 13, fontWeight: 'bold' }}
                      >
                        {nameToInitials(person.name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {person.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {person.rank}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {alUsed} / {currentYearTotal} days used
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={alRemaining <= 2 ? 'error.main' : alRemaining <= 5 ? 'warning.main' : 'success.main'}
                    >
                      {alRemaining} days remaining
                    </Typography>
                    {carryoverActive && (
                      <Chip
                        label={`+${carryover}d carry (exp ${carryoverExpiry!.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`}
                        size="small"
                        color="warning"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {tilHours}h
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {calculateTILDays(tilHours)} available
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {warnings.length === 0 ? (
                        <Chip label="OK" size="small" color="success" />
                      ) : (
                        warnings.map((w, i) => (
                          <Chip key={i} label={w} size="small" color="warning" icon={<Warning />} />
                        ))
                      )}
                    </Box>
                  </TableCell>

                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      <Tooltip title="View full profile">
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNew fontSize="small" />}
                          onClick={() => navigate(`/admin/leave-management/staff/${person._id}`)}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          View
                        </Button>
                      </Tooltip>
                      <Tooltip title="More actions">
                        <IconButton size="small" onClick={(e) => openMenu(e, person._id)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { if (menuStaffId) navigate(`/admin/leave-management/staff/${menuStaffId}`); closeMenu(); }}>
          <OpenInNew fontSize="small" sx={{ mr: 1.5 }} />
          View Profile
        </MenuItem>
        <MenuItem onClick={() => menuStaffId && openEmailDialog(menuStaffId, 'annual-leave')}>
          <MailOutline fontSize="small" sx={{ mr: 1.5, color: 'primary.main' }} />
          Email re: Annual Leave
        </MenuItem>
        <MenuItem onClick={() => menuStaffId && openEmailDialog(menuStaffId, 'til')}>
          <Schedule fontSize="small" sx={{ mr: 1.5, color: 'secondary.main' }} />
          Email re: TIL
        </MenuItem>
        <MenuItem onClick={() => menuStaffId && handleAddTILClick(menuStaffId)}>
          <Add fontSize="small" sx={{ mr: 1.5, color: 'success.main' }} />
          Add TIL
        </MenuItem>
      </Menu>

      {/* Email Dialog */}
      <Dialog
        open={emailDialog.open}
        onClose={() => setEmailDialog(DEFAULT_EMAIL)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {emailDialog.type === 'annual-leave' && `Email ${emailDialog.staffName} — Annual Leave`}
          {emailDialog.type === 'til' && `Email ${emailDialog.staffName} — Time in Lieu`}
          {emailDialog.type === 'custom' && `Email ${emailDialog.staffName}`}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Subject"
            value={emailDialog.subject}
            onChange={e => setEmailDialog(d => ({ ...d, subject: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Message"
            value={emailDialog.message}
            onChange={e => setEmailDialog(d => ({ ...d, message: e.target.value }))}
            fullWidth
            multiline
            rows={7}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailDialog(DEFAULT_EMAIL)} disabled={emailSending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={emailSending || !emailDialog.subject.trim() || !emailDialog.message.trim()}
          >
            {emailSending ? 'Sending…' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddTILModal
        open={addTILModalOpen}
        staffName={staff.find(s => s._id === selectedStaffId)?.name || ''}
        onClose={() => {
          setAddTILModalOpen(false);
          setSelectedStaffId(null);
        }}
        onConfirm={handleAddTILConfirm}
      />
    </Box>
  );
};

export default StaffBalancesTab;
